import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLang } from "@/context/LanguageContext";
import useSeo from "@/hooks/useSeo";
import { ArrowRight } from "lucide-react";

const STR = {
  de: {
    title: "Seite nicht gefunden",
    text: "Die gesuchte Seite existiert nicht oder wurde verschoben. Kehren Sie zur Startseite zurück oder entdecken Sie unseren aktuellen Fuhrpark.",
    home: "Zur Startseite",
    fleet: "Zum Fuhrpark",
  },
  en: {
    title: "Page not found",
    text: "The page you are looking for does not exist or has been moved. Return to the homepage or explore our current fleet.",
    home: "Back to homepage",
    fleet: "View fleet",
  },
  es: {
    title: "Página no encontrada",
    text: "La página que buscas no existe o ha sido movida. Vuelve a la página principal o descubre nuestra flota actual.",
    home: "Ir al inicio",
    fleet: "Ver vehículos",
  },
};

export default function NotFound() {
  const { lang } = useLang();
  const s = STR[lang] || STR.de;
  useSeo({
    title: "404 · Seite nicht gefunden | DNA Automotive",
    description: "Diese Seite existiert nicht. Zurück zur Startseite von DNA Automotive Köln.",
    path: "/404",
  });
  useEffect(() => window.scrollTo(0, 0), []);

  return (
    <div className="bg-obsidian min-h-screen flex flex-col" data-testid="not-found-page">
      <Header />
      <div className="flex-1 flex items-center justify-center px-6 pt-32 pb-20">
        <div className="text-center max-w-xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="font-heading font-black text-brand-bright text-[7rem] md:text-[10rem] leading-none tracking-tighter" data-testid="not-found-code">
              404
            </div>
            <div className="w-16 h-1 bg-brand-light mx-auto my-6" />
            <h1 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-4xl mb-4">
              {s.title}
            </h1>
            <p className="text-zinc-400 leading-relaxed mb-10">{s.text}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/"
                data-testid="not-found-home"
                className="group inline-flex items-center justify-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-8 py-4 hover:bg-brand-hover transition-colors"
              >
                {s.home}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/fahrzeuge"
                data-testid="not-found-fleet"
                className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-bold uppercase tracking-wider text-sm px-8 py-4 hover:border-brand-light hover:text-brand-bright transition-colors"
              >
                {s.fleet}
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
